import React, { useEffect, useState } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { axiosInstance } from "../../utils/util.axios";
import lottie from "lottie-web";
import successData from "../../assets/loading-credentials.json";
import styles from "./SSOLogin.module.scss";
import { handleSSOError } from "../../store/actions/GeneralActions";
import Loading from "../../components/SSO/Loading";
import BookACall from "../../components/BookACall/BookACall";
import FormRegisterStepTwoSSO from "../../components/Forms/FormRegisterStepTwoSSO";
import logoSvg from "../../assets/images/logo_white.svg";
import { activateUserWithCode, logout } from "../../store/actions";
import { LOADING } from "../../store/actions/types";

/**
 * SSOLoginCallback component handles the login redirect from Keycloak.
 * It processes the authorization code, validates access, and either:
 * - Redirects the user to the main Platform if allowed
 * - Shows an extra registration step to complete onboarding if not allowed to access Platform
 *
 * The component manages loading animations, error handling, and UI transitions
 * between various authentication states based on server response.
 *
 * @component
 * @returns {JSX.Element} The rendered login callback handler and/or Keycloak 2nd registration step.
 */
const SSOLoginCallback = () => {
    const [isLoading, setIsLoading] = useState(true);
    const { search } = useLocation();
    const history = useHistory();
    const dispatch = useDispatch();
    const queryParams = new URLSearchParams(search);
    const codeParam = queryParams.get("code");
    const state = useSelector((state) => state.general);
    const [bookACallModalDisplayed, setBookACallModalDisplayed] = useState(false);

    /**
     * Manage the callbacks from Keycloak. Called after a successful login/registration.
     * 
     * @param {string} code Keycloak's authenticcation code. Can be used only once after login.
     */
    const redirectToPlatform = async (code) => {
        try {
            // need to set withCredentials option to store the cookie
            const res = await axiosInstance.get(`/auth_callback?code=${code}`, { withCredentials: true });
            const data = res.data;
            if (data.success === 1) {
                // Successful login. Evaluate if the user can access Platform
                localStorage.setItem("session_active", data.token);
                localStorage.setItem("qmenta-global-state", JSON.stringify(data));
                axiosInstance.defaults.headers.common["x-session-verify"] = localStorage.getItem("session_active");

                // For users that exists in Keycloak but can't access Platform, we must allow them
                // to either Book a Call or use an activation code. In order to authenticate as a
                // Keycloak user again, we can't use the same code that has been used for
                // auth_callback, so we need to store a valid token
                if (data.error !== "User can't access Platform") {
                    // User can access Platform, redirect to Dojo
                    window.location.replace(process.env.REACT_APP_FRONTEND_URL);
                }
            } else if (data.error === "User doesn't exist in Platform") {
                // Do not display the book a call page to users that don't exist, since they may
                // have been recently registered in Keycloak and it's data hasn't been transferred
                // yet to Platform
                history.push("/");
            }
            setIsLoading(false);
        } catch (error) {
            setIsLoading(false);
            dispatch(handleSSOError("Error during Login"));
            history.push("/");
        }
    };

    /**
     * Toggle between displaying or hidding the Book a Call model.
     */
    const toggleBookACallModal = () => {
        setBookACallModalDisplayed(prev => !prev);
    };

    const redirectToHubspot = (setBookACallButtonEvent) => {
        // In the 'Book a Call' modal, the user has clicked the 'Book a Call' button.
        // Logout the user + redirection to hubspot
        setBookACallButtonEvent(LOADING);
        dispatch(logout("https://meetings.hubspot.com/paulo17"));
    };

    /**
     * On RegisterWithCode button clicked. Simply logout and get redirected to Keycloak again.
     * 
     * @param {string} activationCode Value introduced by the user to send to the backend
     * @param {Function} setRegisterButtonEvent Callback to manage the RegisterButton status.
     */
    const onClickRegisterWithCode = (activationCode, setRegisterButtonEvent) => {
        let formData = new FormData();
        formData.append("activation_code", activationCode);
        dispatch(activateUserWithCode(formData, state.errorAttempts, setRegisterButtonEvent));
    };

    /**
     * On Log In button clicked. Simply logout and get redirected to Keycloak again
     */
    const onClickLogIn = () => {
        dispatch(logout());
    };

    useEffect(() => {
        lottie.loadAnimation({
            container: document.querySelector("#callbackLottie"),
            animationData: successData,
            loop: true,
            autoplay: true
        });

        if (codeParam) {
            redirectToPlatform(codeParam);
            return;
        }
        setIsLoading(false);
        dispatch(handleSSOError("Invalid URL"));
        history.push("/");
    }, []);

    if (isLoading) {
        return (
            <div className={styles.container}>
                <Loading id="callbackLottie" />
            </div>
        );
    } else {
        // User didn't get redirected to frontend-dojo. Display the Book a Call page
        return (
            <div className={styles.kcBodyClass}>
                {/* Book a call modal */}
                {bookACallModalDisplayed && (
                    <BookACall
                        onClickBookACall={redirectToHubspot}
                        onClickCancelButton={toggleBookACallModal}
                    />
                )}

                <div className={styles.loginContainer}>

                    {/* Left side */}
                    <div className={styles.leftSide}>
                        <img className={styles.leftLogo} src={logoSvg} alt="QMENTA Platform" width={239} />
                        <h2 className={styles.leftTextHeader}>
                            Optimize Your Clinical Research With Our Advanced Imaging Management System
                        </h2>
                        <h3 className={styles.leftTextSmall}>
                            Our end-to-end solution streamlines the process of data collection in a consistent, compliant way.
                        </h3>
                    </div>
                    {/* Right side */}
                    <div className={styles.rightSide}>
                        <FormRegisterStepTwoSSO
                            showBookACallModal={toggleBookACallModal}
                            onClickRegisterWithCode={onClickRegisterWithCode}
                            onClickLogIn={onClickLogIn}
                        />
                    </div>
                </div>
            </div>
        );
    }
};

export default SSOLoginCallback;